import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../config/api";
import {
  FaUsers,
  FaTrash,
  FaSearch,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt
} from "react-icons/fa";
import "../styles/ManageUsers.css";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  /* ================= FETCH USERS ================= */
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_ENDPOINTS.USER_ALL);
      const data = await res.json();

      if (Array.isArray(data)) {
        setUsers(data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE USER ================= */
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this user?"
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(`${API_ENDPOINTS.USER_PROFILE}/${id}`, {
        method: "DELETE",
      });

      const data = await res.json();

      if (res.ok) {
        setUsers((prevUsers) => prevUsers.filter((user) => user._id !== id));
        alert("🎉 User deleted successfully");
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= FILTER LOGIC ================= */
  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    return (
      user.fullName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.mobile?.toLowerCase().includes(query) ||
      user.location?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="manage-users-container">
      {/* BANNER SECTION */}
      <div className="users-banner">
        <div className="banner-content">
          <h1>👥 Registered Users Directory</h1>
          <p>View profile listings, search user records, check details, and manage community accounts.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaUsers className="icon" />
            <span><strong>{users.length}</strong> Registered Users</span>
          </div>
        </div>
      </div>

      {/* SEARCH & FILTERS BAR */}
      <div className="dashboard-controls">
        <div className="search-filter-box">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search users by name, email, phone, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div className="panel-container">
        
        {loading ? (
          <div className="users-loading">
            <div className="spinner"></div>
            <h2>Fetching Users Directory...</h2>
            <p>Syncing profiles database</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <FaUsers className="empty-icon" />
            <h3>No Users Found</h3>
            <p>{searchQuery ? "Try adjusting your search terms." : "There are currently no registered users on the platform."}</p>
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE VIEW */}
            <div className="users-table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>User Details</th>
                    <th>Mobile</th>
                    <th>Location</th>
                    <th>About Bio</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user._id}>
                      {/* PROFILE PHOTO */}
                      <td>
                        <div className="avatar-wrapper">
                          <img
                            src={user.profileImage || "/images/default-user.png"}
                            alt={user.fullName}
                            className="user-image"
                            onError={(e) => {
                              e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                            }}
                          />
                        </div>
                      </td>

                      {/* USER DETAILS */}
                      <td>
                        <div className="user-info-cell">
                          <span className="user-full-name">{user.fullName || "N/A"}</span>
                          <span className="user-email-address"><FaEnvelope className="mini-icon" /> {user.email || "N/A"}</span>
                        </div>
                      </td>

                      {/* MOBILE */}
                      <td>
                        <span className="user-mobile-num">
                          {user.mobile ? (
                            <>
                              <FaPhoneAlt className="mini-icon" /> {user.mobile}
                            </>
                          ) : (
                            <span className="na-tag">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* LOCATION */}
                      <td>
                        <span className="user-location-text">
                          {user.location ? (
                            <>
                              <FaMapMarkerAlt className="mini-icon" style={{ color: "#ef4444" }} /> {user.location}
                            </>
                          ) : (
                            <span className="na-tag">N/A</span>
                          )}
                        </span>
                      </td>

                      {/* BIO */}
                      <td className="user-bio">
                        <span className="bio-text" title={user.bio}>
                          {user.bio || <em className="no-bio-tag">No bio added</em>}
                        </span>
                      </td>

                      {/* ACTIONS */}
                      <td>
                        <button
                          className="user-delete-btn"
                          onClick={() => handleDelete(user._id)}
                          title="Delete user profile"
                        >
                          <FaTrash /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* RESPONSIVE MOBILE CARDS VIEW */}
            <div className="users-mobile-grid">
              {filteredUsers.map((user) => (
                <div className="user-mobile-card" key={user._id}>
                  <div className="mobile-card-header">
                    <img
                      src={user.profileImage || "/images/default-user.png"}
                      alt={user.fullName}
                      className="user-image-mobile"
                      onError={(e) => {
                        e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                      }}
                    />
                    <div className="mobile-card-header-info">
                      <h4>{user.fullName || "N/A"}</h4>
                      <span>{user.email || "N/A"}</span>
                    </div>
                    <button className="mobile-delete-btn" onClick={() => handleDelete(user._id)}>
                      <FaTrash />
                    </button>
                  </div>

                  <div className="mobile-card-body">
                    <div className="mobile-detail-item">
                      <FaPhoneAlt className="icon" />
                      <span>{user.mobile || <span className="na-tag">N/A</span>}</span>
                    </div>
                    <div className="mobile-detail-item">
                      <FaMapMarkerAlt className="icon" style={{ color: "#ef4444" }} />
                      <span>{user.location || <span className="na-tag">N/A</span>}</span>
                    </div>
                    <div className="mobile-bio-item">
                      <h5>About Bio</h5>
                      <p>{user.bio || <em className="no-bio-tag">No bio added</em>}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManageUsers;
